import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { labRequestsAPI } from '../lib/api';
import { generateLabRequestPDF } from '../lib/pdfGenerator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Loader2,
  FlaskConical,
  FileDown,
  Mail,
  Printer,
  AlertCircle,
  CheckCircle2,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

// Lab test categories - must match backend
const LAB_TEST_CATEGORIES = {
  haematology: {
    name: "Haematology",
    tests: ["CBC", "Manual Differential", "ESR", "Blood Film", "PT", "PTT", "INR", "D-Dimer", "Reticulocyte Count", "Hb Electrophoresis", "CD4 Count"]
  },
  blood_bank: {
    name: "Blood Bank",
    tests: ["Blood Group", "RH", "Direct Coombs Test", "Indirect Coombs Test"]
  },
  urine_chemistry: {
    name: "Urine Chemistry",
    tests: ["Creatinine Clearance", "24 Hour Urine Protein", "Microalbumin", "Amylase", "Spot Creatinine", "Na", "K"]
  },
  csf_chemistry: {
    name: "CSF Chemistry",
    tests: ["Glucose", "Protein"]
  },
  fluid_chemistry: {
    name: "Fluid Chemistry",
    tests: ["LDH", "Protein", "Other"]
  },
  renal: {
    name: "Serum Chemistry - Renal",
    tests: ["BUN", "Creatinine", "Na", "K", "Cl", "CO2"]
  },
  liver: {
    name: "Serum Chemistry - Liver",
    tests: ["Total Protein", "Albumin", "Globulin", "Total Bilirubin", "Direct Bilirubin", "ALP", "AST", "ALT", "GGT"]
  },
  cardiac_enzymes: {
    name: "Cardiac Enzymes",
    tests: ["CK", "LDH", "Troponin", "High Sensitivity Troponin"]
  },
  glucose: {
    name: "Glucose",
    tests: ["Fasting", "Random", "1 Hour", "2 Hour", "OGTT"]
  },
  lipid_profile: {
    name: "Lipid Profile",
    tests: ["Total Cholesterol", "Triglycerides", "HDL", "LDL", "VLDL"]
  },
  thyroid: {
    name: "Thyroid",
    tests: ["TSH", "FT3", "FT4"]
  },
  tumour_markers: {
    name: "Tumour Markers",
    tests: ["PSA", "CEA", "CA 125", "CA 19.9", "CA 15.3"]
  },
  other_chemistry: {
    name: "Other Chemistry",
    tests: ["HbA1c", "Amylase", "Calcium", "Phosphorus", "Uric Acid", "Beta hCG"]
  },
  microbiology: {
    name: "Microbiology / Bacteriology",
    tests: ["Urinalysis", "Ova Cysts Parasites", "Gram Stain", "CSF Cell Count", "Fluid Cell Count", "Culture & Sensitivity", "Semen Analysis", "AFB"]
  },
  serology: {
    name: "Serology",
    tests: ["ASOT", "Monospot", "LE", "RA", "H Pylori", "ANA", "HTLV I/II", "HBsAg", "HIV I/II", "Anti-HCV", "Syphilis"]
  },
  molecular_biology: {
    name: "Molecular Biology",
    tests: ["Tuberculosis", "HIV Viral Load", "HPV", "HPV Genotype 16/18", "Chlamydia/Gonorrhea", "Trichomonas", "Herpes Simplex I/II", "H. pylori"]
  },
  communicable_disease: {
    name: "Communicable Disease",
    tests: ["COVID-19", "Leptospirosis", "Zika", "Influenza A/B", "Chikungunya", "Dengue", "EBV", "CMV", "RSV", "Rotavirus", "Toxoplasma"]
  },
  tb_testing: {
    name: "TB Testing",
    tests: ["Microscopy", "Xpert MTB/RIF", "Culture", "Drug Susceptibility Testing"]
  },
  blood_culture: {
    name: "Blood Culture",
    tests: ["Blood Culture"]
  }
};

// Fasting-required tests
const FASTING_TESTS = ['Fasting', 'OGTT', 'Total Cholesterol', 'Triglycerides', 'HDL', 'LDL', 'VLDL'];

export default function LabRequestDialog({ open, onOpenChange, patient, onSuccess }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTests, setSelectedTests] = useState({});
  const [activeTab, setActiveTab] = useState('haematology');
  
  // Form fields
  const [formData, setFormData] = useState({
    diagnosis: '',
    clinical_indication: '',
    ordering_physician: '',
    physician_phone: '',
    bill_to: '',
    is_pregnant: false,
    is_fasting: false,
    fasting_hours: 12,
    on_anticoagulants: false,
    anticoagulant_drug: '',
    specimen_collection_date: '',
    specimen_collection_time: '',
    phlebotomist: '',
    notes: '',
    priority: 'routine',
  });

  // Initialize form with user data
  useEffect(() => {
    if (open && user) {
      setFormData(prev => ({
        ...prev,
        ordering_physician: user.full_name || '',
        physician_phone: user.personal_phone || '',
      }));
      setSelectedTests({});
      setSearchQuery('');
    }
  }, [open, user]);

  // Check if any fasting test is selected
  useEffect(() => {
    const allSelectedTests = Object.values(selectedTests).flat();
    const hasFastingTest = allSelectedTests.some(test => FASTING_TESTS.includes(test));
    if (hasFastingTest && !formData.is_fasting) {
      setFormData(prev => ({ ...prev, is_fasting: true }));
    }
  }, [selectedTests]);

  const handleTestToggle = (category, test) => {
    setSelectedTests(prev => {
      const categoryTests = prev[category] || [];
      if (categoryTests.includes(test)) {
        return {
          ...prev,
          [category]: categoryTests.filter(t => t !== test)
        };
      } else {
        return {
          ...prev,
          [category]: [...categoryTests, test]
        };
      }
    });
  };

  const handleSelectAllCategory = (category) => {
    const tests = LAB_TEST_CATEGORIES[category].tests;
    const currentSelected = selectedTests[category] || [];
    
    if (currentSelected.length === tests.length) {
      // Deselect all
      setSelectedTests(prev => ({ ...prev, [category]: [] }));
    } else {
      // Select all
      setSelectedTests(prev => ({ ...prev, [category]: [...tests] }));
    }
  };

  const getTotalSelectedCount = () => {
    return Object.values(selectedTests).flat().length;
  };

  const getFilteredCategories = () => {
    if (!searchQuery) return Object.entries(LAB_TEST_CATEGORIES);
    
    const query = searchQuery.toLowerCase();
    return Object.entries(LAB_TEST_CATEGORIES).filter(([key, category]) => {
      return category.name.toLowerCase().includes(query) ||
        category.tests.some(test => test.toLowerCase().includes(query));
    });
  };

  const handleSubmit = async () => {
    if (getTotalSelectedCount() === 0) {
      toast.error('Please select at least one test');
      return;
    }

    setSaving(true);
    try {
      const requestData = {
        patient_id: patient.id,
        ...formData,
        selected_tests: selectedTests,
      };

      const response = await labRequestsAPI.create(requestData);
      toast.success(`Lab request created: ${response.data.reference_number}`);
      
      // Generate PDF automatically
      await generateLabRequestPDF(response.data, user);
      
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lab request');
    }
    setSaving(false);
  };

  const filteredCategories = getFilteredCategories();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-blue-600" />
            Generate Lab Request
          </DialogTitle>
          <DialogDescription>
            Patient: {patient?.first_name} {patient?.last_name} ({patient?.patient_id})
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="tests" className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="tests">
                Select Tests
                {getTotalSelectedCount() > 0 && (
                  <Badge className="ml-2 bg-blue-600">{getTotalSelectedCount()}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="clinical">Clinical Info</TabsTrigger>
              <TabsTrigger value="specimen">Specimen Details</TabsTrigger>
            </TabsList>

            {/* Tests Selection Tab */}
            <TabsContent value="tests" className="flex-1 overflow-hidden mt-4">
              <div className="space-y-4 h-full flex flex-col">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search tests..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Priority */}
                <div className="flex items-center gap-4">
                  <Label>Priority:</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Test Categories */}
                <ScrollArea className="flex-1 pr-4" style={{ height: '350px' }}>
                  <div className="grid grid-cols-2 gap-4">
                    {filteredCategories.map(([categoryKey, category]) => {
                      const categorySelected = selectedTests[categoryKey] || [];
                      const allSelected = categorySelected.length === category.tests.length;
                      const someSelected = categorySelected.length > 0 && !allSelected;
                      
                      // Filter tests if searching
                      const filteredTests = searchQuery
                        ? category.tests.filter(test => 
                            test.toLowerCase().includes(searchQuery.toLowerCase()))
                        : category.tests;
                      
                      if (searchQuery && filteredTests.length === 0) return null;

                      return (
                        <div key={categoryKey} className="border rounded-lg p-3 bg-slate-50">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="font-semibold text-slate-700">{category.name}</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAllCategory(categoryKey)}
                              className="text-xs h-6"
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                          </div>
                          <div className="space-y-1">
                            {filteredTests.map(test => {
                              const isSelected = categorySelected.includes(test);
                              const isFastingTest = FASTING_TESTS.includes(test);
                              
                              return (
                                <div key={test} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`${categoryKey}-${test}`}
                                    checked={isSelected}
                                    onCheckedChange={() => handleTestToggle(categoryKey, test)}
                                  />
                                  <label
                                    htmlFor={`${categoryKey}-${test}`}
                                    className={`text-sm cursor-pointer flex items-center gap-1 ${isSelected ? 'font-medium text-blue-700' : 'text-slate-600'}`}
                                  >
                                    {test}
                                    {isFastingTest && (
                                      <span className="text-xs text-amber-600">(F)</span>
                                    )}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            </TabsContent>

            {/* Clinical Info Tab */}
            <TabsContent value="clinical" className="mt-4">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="diagnosis">Diagnosis / Clinical Indication</Label>
                      <Textarea
                        id="diagnosis"
                        value={formData.diagnosis}
                        onChange={(e) => setFormData(prev => ({ ...prev, diagnosis: e.target.value }))}
                        placeholder="e.g., Diabetes mellitus, Hypertension"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Additional Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any special instructions..."
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ordering_physician">Ordering Physician</Label>
                      <Input
                        id="ordering_physician"
                        value={formData.ordering_physician}
                        onChange={(e) => setFormData(prev => ({ ...prev, ordering_physician: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="physician_phone">Physician Phone</Label>
                      <Input
                        id="physician_phone"
                        value={formData.physician_phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, physician_phone: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="bill_to">Bill To</Label>
                    <Input
                      id="bill_to"
                      value={formData.bill_to}
                      onChange={(e) => setFormData(prev => ({ ...prev, bill_to: e.target.value }))}
                      placeholder="Patient / Insurance / Third Party"
                    />
                  </div>

                  {/* Patient Conditions */}
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <Label className="font-semibold mb-3 block">Patient Conditions</Label>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="is_pregnant"
                          checked={formData.is_pregnant}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_pregnant: checked }))}
                        />
                        <label htmlFor="is_pregnant" className="text-sm">Pregnant</label>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_fasting"
                            checked={formData.is_fasting}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_fasting: checked }))}
                          />
                          <label htmlFor="is_fasting" className="text-sm">Fasting Required</label>
                        </div>
                        {formData.is_fasting && (
                          <Input
                            type="number"
                            value={formData.fasting_hours}
                            onChange={(e) => setFormData(prev => ({ ...prev, fasting_hours: parseInt(e.target.value) || 12 }))}
                            className="w-24"
                            placeholder="Hours"
                          />
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="on_anticoagulants"
                            checked={formData.on_anticoagulants}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, on_anticoagulants: checked }))}
                          />
                          <label htmlFor="on_anticoagulants" className="text-sm">On Anticoagulants</label>
                        </div>
                        {formData.on_anticoagulants && (
                          <Input
                            value={formData.anticoagulant_drug}
                            onChange={(e) => setFormData(prev => ({ ...prev, anticoagulant_drug: e.target.value }))}
                            placeholder="Drug name"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Specimen Details Tab */}
            <TabsContent value="specimen" className="mt-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="specimen_collection_date">Collection Date</Label>
                    <Input
                      id="specimen_collection_date"
                      type="date"
                      value={formData.specimen_collection_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, specimen_collection_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="specimen_collection_time">Collection Time</Label>
                    <Input
                      id="specimen_collection_time"
                      type="time"
                      value={formData.specimen_collection_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, specimen_collection_time: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phlebotomist">Phlebotomist</Label>
                  <Input
                    id="phlebotomist"
                    value={formData.phlebotomist}
                    onChange={(e) => setFormData(prev => ({ ...prev, phlebotomist: e.target.value }))}
                    placeholder="Name of phlebotomist"
                  />
                </div>

                {/* Fasting Instructions */}
                {formData.is_fasting && (
                  <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-amber-800">Fasting Instructions</h4>
                        <ul className="text-sm text-amber-700 mt-1 space-y-1">
                          <li>• Patient must fast for {formData.fasting_hours} hours before specimen collection</li>
                          <li>• Water is permitted during fasting period</li>
                          <li>• Avoid alcohol for 24 hours before lipid tests</li>
                          <li>• Continue regular medications unless otherwise instructed</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected Tests Summary */}
                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Selected Tests Summary ({getTotalSelectedCount()} tests)
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {Object.entries(selectedTests).map(([category, tests]) => {
                      if (!tests || tests.length === 0) return null;
                      const categoryName = LAB_TEST_CATEGORIES[category]?.name || category;
                      return (
                        <div key={category} className="text-sm">
                          <span className="font-medium text-blue-700">{categoryName}:</span>{' '}
                          <span className="text-blue-600">{tests.join(', ')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-slate-500">
              {getTotalSelectedCount()} test{getTotalSelectedCount() !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving || getTotalSelectedCount() === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4 mr-2" />
                    Create & Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
